/**
 * useEncryptedPDF
 *
 * Owns the encrypted-PDF / suggested-password flow that was previously spread
 * across class methods on FormViewer:
 *
 *   handleEncryptedPDFSelected()          — opens save-suggested-password dialog
 *   handleEncryptedPDFSelectedSecondStep() — sends encrypted ReturnPrint cmd
 *   handleSaveSuggestedPassword()          — triggers save then opens step-2 dialog
 *   handleSaveSuggestedPasswordDoc()       — emails / texts the encrypted doc
 *   handleNewEncryptedPassword(...)        — orchestrates the full flow
 *
 * Because the functions read many rapidly-changing Redux state values at
 * call-time (encryptedPassword, email addresses, checkbox states, etc.),
 * the caller must keep `encryptedPdfCallbacksRef.current` up-to-date on
 * every render with the current Redux prop snapshot.
 *
 * encryptedPdfCallbacksRef.current shape:
 *   encryptedPassword, encryptedPdfTitle, emailBlob, attachmentContent
 *   taxpayerTextChecked, spouseTextChecked
 *   taxpayerCellPhoneNumber, taxpayerCellPhoneCarrier, taxpayerCellPhoneDomain
 *   spouseCellPhoneNumber, spouseCellPhoneCarrier, spouseCellPhoneDomain
 *   taxpayerChecked, taxpayerEPassChecked, taxpayerEmailForPdf
 *   spouseChecked, spouseEPassChecked, spouseEmailForPdf
 *   representativeChecked, representativeEPassChecked, preparerEmailForPdf
 *   isReadOnly
 */
import { useCallback } from 'react';
import XlinkAPI from '~/app/api/xlinkAPI';
import ErrorHelpers from '~/app/errorHelpers.js';
import { PRINT_TYPES } from '~/app/constants';
import { ARCHIVE_EMAIL_BODY } from '~/app/Pages/Returns/components/Document/ArchivedDocuments/archivedDocumentsConstants.js';

const useEncryptedPDF = ({
  calcAPIRef,
  encryptedPdfCallbacksRef, // kept-current snapshot of changing Redux state
  // Stable hook objects (passed by ref internally — always current via the wrapper's ref pattern)
  printRef, // kept-current ref to usePrint result
  returnCloseRef, // kept-current ref to useReturnClose result
  textLinkRef, // kept-current ref to useTextLink result
  documentAttachRef, // kept-current ref to useDocumentAttach result
  spinnerRef, // kept-current ref to useSpinner result
  // Stable Redux action dispatchers (never change identity)
  toggleSaveSuggestedPassword,
  setEmailBlob,
  setEncryptedDocumentContent,
  setEncryptedPdfRequest,
  toggleSuggestedPassword,
  setEncryptedPdfTitle,
  setSuggestedPassword,
  toggleEncryptedPdf,
  setEncryptedPDFSuccessDialog,
  setSentSuccessTitle,
  setSentSuccessMsg,
  setTaxpayerSelected,
  setTaxpayerSelectedEPass,
  setTaxpayerSelectedText,
  setSpouseSelected,
  setSpouseSelectedEPass,
  setSpouseSelectedText,
  representativeSelected,
  representativeSelectedEPass,
  representativeSelectedText,
  setArchiveDocId,
}) => {
  // ─── step 2: send encrypted print cmd ────────────────────────────────────

  const handleEncryptedPDFSelectedSecondStep = useCallback(() => {
    const cb = encryptedPdfCallbacksRef.current;
    const print = printRef.current;

    setEmailBlob('');
    setEncryptedDocumentContent('');
    setEncryptedPdfRequest(true);
    print.setShowFinalPDF(false);

    const type = print.print.pType;
    const prefs =
      type === PRINT_TYPES.RETURN_COMPONENTS
        ? print.print.overridePrefs
        : print.convertPrintPrefs(print.print.prefs, type);
    const formOccur = print.print.formOccur;
    const pAction = '04';
    const password = cb.encryptedPassword;

    if (formOccur !== 'State') {
      calcAPIRef.current.ReturnPrint(
        type,
        prefs,
        formOccur,
        {},
        pAction,
        password,
        print.print.cltr,
        cb.isReadOnly,
      );
    } else {
      calcAPIRef.current.ReturnPrintStateClientLetter(
        type,
        prefs,
        formOccur,
        {},
        pAction,
        password,
        print.print.cltr,
        print.print.val,
        cb.isReadOnly,
      );
    }
  }, [
    calcAPIRef,
    encryptedPdfCallbacksRef,
    printRef,
    setEmailBlob,
    setEncryptedDocumentContent,
    setEncryptedPdfRequest,
  ]);

  // ─── open save-suggested-password dialog (step 1) ────────────────────────

  const handleEncryptedPDFSelected = useCallback(() => {
    toggleSaveSuggestedPassword(true);
  }, [toggleSaveSuggestedPassword]);

  // ─── user confirms save → trigger ReturnSave which opens step 2 ──────────

  const handleSaveSuggestedPassword = useCallback(() => {
    printRef.current.setEncryptedPdfPrint(true);
    toggleSaveSuggestedPassword(false);
    returnCloseRef.current.saveReturn();
  }, [printRef, returnCloseRef, toggleSaveSuggestedPassword]);

  // ─── email / text the encrypted document ─────────────────────────────────

  const handleSaveSuggestedPasswordDoc = useCallback(() => {
    const cb = encryptedPdfCallbacksRef.current;
    const print = printRef.current;

    const encryptedPassword = cb.encryptedPassword !== undefined ? cb.encryptedPassword : '';
    const sendMessage = 'The document was password protected. The password is ' + encryptedPassword;
    const newMessage = sendMessage.toString();

    if (cb.taxpayerTextChecked && encryptedPassword !== '') {
      textLinkRef.current.handleSendTextLinkMessage(
        'Name',
        cb.taxpayerCellPhoneNumber,
        cb.taxpayerCellPhoneCarrier,
        newMessage,
        cb.taxpayerCellPhoneDomain,
      );
    }
    if (cb.spouseTextChecked && encryptedPassword !== '') {
      textLinkRef.current.handleSendTextLinkMessage(
        'Name',
        cb.spouseCellPhoneNumber,
        cb.spouseCellPhoneCarrier,
        newMessage,
        cb.spouseCellPhoneDomain,
      );
    }

    if (
      cb.taxpayerChecked ||
      (cb.taxpayerEPassChecked && encryptedPassword !== '') ||
      cb.spouseChecked ||
      (cb.spouseEPassChecked && encryptedPassword !== '')
    ) {
      const tpEmail = cb.taxpayerChecked || cb.taxpayerEPassChecked ? cb.taxpayerEmailForPdf : '';
      const spEmail = cb.spouseChecked || cb.spouseEPassChecked ? cb.spouseEmailForPdf : '';
      const repEmail =
        cb.representativeChecked || cb.representativeEPassChecked ? cb.preparerEmailForPdf : '';
      const emailSubject = cb.encryptedPdfTitle;
      const password = encryptedPassword;
      const emailBody = btoa(cb.emailBlob);
      const attachmentContent = cb.attachmentContent;
      const preparerID = documentAttachRef.current.preparerID;
      const tpEmailChecked = cb.taxpayerChecked ?? false;
      const spEmailChecked = cb.spouseChecked ?? false;
      const repEmailChecked = cb.representativeChecked ?? false;
      const tpEmailPassChecked = cb.taxpayerEPassChecked ?? false;
      const spEmailPassChecked = cb.spouseEPassChecked ?? false;
      const repEmailPassChecked = cb.representativeEPassChecked ?? false;

      XlinkAPI.emailEncryptedDoc(
        tpEmail,
        spEmail,
        repEmail,
        emailSubject,
        password,
        emailBody,
        attachmentContent,
        preparerID,
        tpEmailChecked,
        spEmailChecked,
        repEmailChecked,
        tpEmailPassChecked,
        spEmailPassChecked,
        repEmailPassChecked,
      )
        .then(() => {
          toggleEncryptedPdf(false);
          setEncryptedPDFSuccessDialog(true);
          print.setShowFinalPDF(true);
        })
        .catch(error => {
          ErrorHelpers.handleError('Error Sending Encrypted PDF', error);
        });
    }
  }, [
    encryptedPdfCallbacksRef,
    printRef,
    textLinkRef,
    documentAttachRef,
    toggleEncryptedPdf,
    setEncryptedPDFSuccessDialog,
  ]);

  // ─── orchestrate the full flow from the SuggestedPasswordDialog ──────────

  const handleNewEncryptedPassword = useCallback(
    async (
      newEncryptedPassword,
      encryptedPdfTitle,
      taxpayerSelected,
      spouseSelected,
      representativeSelectedVal,
      taxpayerSelectedEPass,
      spouseSelectedEPass,
      representativeSelectedEPassVal,
      taxpayerSelectedText,
      spouseSelectedText,
      representativeSelectedTextVal,
    ) => {
      spinnerRef.current.startSpin();
      setTaxpayerSelected(taxpayerSelected);
      setTaxpayerSelectedEPass(taxpayerSelectedEPass);
      setTaxpayerSelectedText(taxpayerSelectedText);
      setSpouseSelected(spouseSelected);
      setSpouseSelectedEPass(spouseSelectedEPass);
      setSpouseSelectedText(spouseSelectedText);
      representativeSelected(representativeSelectedVal);
      representativeSelectedEPass(representativeSelectedEPassVal);
      representativeSelectedText(representativeSelectedTextVal);

      await setSuggestedPassword(newEncryptedPassword);
      setEncryptedPdfTitle(encryptedPdfTitle);
      toggleSuggestedPassword(false);

      const cb = encryptedPdfCallbacksRef.current;

      if (cb.archiveDocId) {
        const sendingDoc = taxpayerSelected || spouseSelected || representativeSelectedVal;
        setSentSuccessTitle(sendingDoc ? 'Attached Document Sent' : 'Password Sent');
        setSentSuccessMsg(
          sendingDoc
            ? 'The attached document has been sent successfully'
            : 'The password has been sent successfully',
        );

        const tpEmail = taxpayerSelected || taxpayerSelectedEPass ? cb.taxpayerEmailForPdf : '';
        const spEmail = spouseSelected || spouseSelectedEPass ? cb.spouseEmailForPdf : '';
        const repEmail =
          representativeSelectedVal || representativeSelectedEPassVal ? cb.preparerEmailForPdf : '';

        // Text the password to any recipients who selected the "Text Password to" option.
        // TextLink delivery is a frontend SMS-gateway operation, independent of the
        // archive email API, mirroring handleSaveSuggestedPasswordDoc for the print flow.
        if (newEncryptedPassword !== '') {
          const textMessage = `The document was password protected. The password is ${newEncryptedPassword}`;
          if (taxpayerSelectedText) {
            textLinkRef.current.handleSendTextLinkMessage(
              'Name',
              cb.taxpayerCellPhoneNumber,
              cb.taxpayerCellPhoneCarrier,
              textMessage,
              cb.taxpayerCellPhoneDomain,
            );
          }
          if (spouseSelectedText) {
            textLinkRef.current.handleSendTextLinkMessage(
              'Name',
              cb.spouseCellPhoneNumber,
              cb.spouseCellPhoneCarrier,
              textMessage,
              cb.spouseCellPhoneDomain,
            );
          }
        }

        const hasEmailRecipient =
          (tpEmail !== '' && (taxpayerSelected || taxpayerSelectedEPass)) ||
          (spEmail !== '' && (spouseSelected || spouseSelectedEPass)) ||
          (repEmail !== '' && (representativeSelectedVal || representativeSelectedEPassVal));

        if (!hasEmailRecipient) {
          // Text-only send: no email API call needed; complete the UI flow locally.
          setArchiveDocId('');
          toggleEncryptedPdf(false);
          setEncryptedPDFSuccessDialog(true);
          spinnerRef.current.stopSpin();
          return;
        }

        XlinkAPI.emailEncryptedArchiveDoc({
          documentID: cb.archiveDocId,
          returnID: cb.returnID,
          password: newEncryptedPassword,
          emailSubject: encryptedPdfTitle,
          emailBody: btoa(cb.emailBlob || ARCHIVE_EMAIL_BODY),
          tpEmail,
          spEmail,
          repEmail,
          tpEmailChecked: taxpayerSelected ?? false,
          spEmailChecked: spouseSelected ?? false,
          repEmailChecked: representativeSelectedVal ?? false,
          tpEmailPassChecked: taxpayerSelectedEPass ?? false,
          spEmailPassChecked: spouseSelectedEPass ?? false,
          repEmailPassChecked: representativeSelectedEPassVal ?? false,
        })
          .then(() => {
            setArchiveDocId('');
            toggleEncryptedPdf(false);
            setEncryptedPDFSuccessDialog(true);
            spinnerRef.current.stopSpin();
          })
          .catch(error => {
            setArchiveDocId('');
            spinnerRef.current.stopSpin();
            ErrorHelpers.handleError('Error Sending Encrypted Archive Document', error);
          });
      } else if (taxpayerSelected || spouseSelected || representativeSelectedVal) {
        setSentSuccessTitle('Attached Document Sent');
        setSentSuccessMsg('The attached document has been sent successfully');
        handleEncryptedPDFSelectedSecondStep();
      } else {
        setSentSuccessTitle('Password Sent');
        setSentSuccessMsg('The password has been sent successfully');
        spinnerRef.current.stopSpin();
        handleSaveSuggestedPasswordDoc();
      }
    },
    [
      spinnerRef,
      textLinkRef,
      encryptedPdfCallbacksRef,
      setTaxpayerSelected,
      setTaxpayerSelectedEPass,
      setTaxpayerSelectedText,
      setSpouseSelected,
      setSpouseSelectedEPass,
      setSpouseSelectedText,
      representativeSelected,
      representativeSelectedEPass,
      representativeSelectedText,
      setSuggestedPassword,
      setEncryptedPdfTitle,
      toggleSuggestedPassword,
      setSentSuccessTitle,
      setSentSuccessMsg,
      handleEncryptedPDFSelectedSecondStep,
      handleSaveSuggestedPasswordDoc,
      setArchiveDocId,
      toggleEncryptedPdf,
      setEncryptedPDFSuccessDialog,
    ],
  );

  return {
    handleEncryptedPDFSelected,
    handleEncryptedPDFSelectedSecondStep,
    handleSaveSuggestedPassword,
    handleSaveSuggestedPasswordDoc,
    handleNewEncryptedPassword,
  };
};

export default useEncryptedPDF;
